import { Hourglass } from "lucide-react";
import { Link } from "react-router-dom";

import { PAYOUT_BATCH_APPROVAL_SETTINGS_PATH } from "@/modules/dashboard/settings/constants/payoutBatchApprovalSection";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

export interface BatchApprovalBannerProps {
  submittedAt: string | null;
  submittedByViewer: boolean;
  /** The organization's policy lets the viewer approve it themselves. */
  viewerMayApprove: boolean;
  /** Members only: staff have no organization settings to read the rule in. */
  linksToSettings: boolean;
}

/** A batch waiting for its second user, and why nothing on the page can be edited. */
export function BatchApprovalBanner({
  submittedAt,
  submittedByViewer,
  viewerMayApprove,
  linksToSettings,
}: BatchApprovalBannerProps) {
  const { t, activeLanguage } = useTranslations("payoutBatches");
  const when = formatDateTime(submittedAt, { locale: activeLanguage, includeTime: true });
  // A maker who must wait for someone else is the one who asks why.
  const waitsForSomeoneElse = submittedByViewer && !viewerMayApprove;

  return (
    <ColocaAlert
      variant="secondary"
      icon={Hourglass}
      heading={t("approval_banner_title")}
      text={
        <>
          {!submittedByViewer
            ? t("approval_banner_text_other", { when })
            : viewerMayApprove
              ? t("approval_banner_text_self_approver", { when })
              : t("approval_banner_text_self", { when })}
          {waitsForSomeoneElse && linksToSettings && (
            <>
              {" "}
              <Link
                to={PAYOUT_BATCH_APPROVAL_SETTINGS_PATH}
                className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
              >
                {t("approval_banner_settings_link")}
              </Link>
            </>
          )}
        </>
      }
    />
  );
}
